import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Flag, ClipboardList, CalendarClock, BellRing, ArrowRight, CheckCircle2 } from "lucide-react";
import client from "@/api/client";

const KIND_ICON = { milestone: Flag, task: ClipboardList, session: CalendarClock, decision: BellRing };

function fmtDue(value, lang) {
  if (!value) return "";
  try {
    const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
    const opts = value.length <= 10
      ? { month: "short", day: "numeric" }
      : { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
    return d.toLocaleDateString(lang || undefined, opts);
  } catch {
    return value;
  }
}

/**
 * ActionCenterCard — "what needs you": everything overdue or due in the next
 * 7 days across success indicators, tasks and mentor sessions, in one place.
 */
export function ActionCenterCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await client.get("/dashboard/action-center");
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Skeleton className="h-28 w-full rounded-2xl" />;
  if (!data) return null;

  const { items = [], counts = {} } = data;
  const clear = items.length === 0;

  return (
    <Card
      className={`rounded-2xl border p-4 sm:p-5 ${counts.overdue > 0 ? "border-rose-500/40 bg-rose-500/[0.04]" : "border-border bg-card"}`}
      data-testid="action-center-card"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${counts.overdue > 0 ? "bg-rose-500/12 text-rose-600" : "bg-primary/10 text-primary"}`}>
          {counts.overdue > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base md:text-lg font-semibold leading-tight" data-testid="action-center-title">
            {t("actionCenter.title", { defaultValue: "What needs you" })}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {clear
              ? t("actionCenter.clear", { defaultValue: "Nothing is overdue or due this week. Enjoy it." })
              : t("actionCenter.subtitle", { defaultValue: "Overdue and due in the next 7 days, across your whole account." })}
          </p>
        </div>
        {!clear && (
          <div className="flex items-center gap-1.5">
            {counts.overdue > 0 && (
              <Badge className="rounded-full bg-rose-500/12 text-rose-600 border-rose-500/30 text-[10px]" data-testid="action-center-overdue">
                {counts.overdue} {t("milestones.overdue", { defaultValue: "overdue" })}
              </Badge>
            )}
            {counts.decisions > 0 && (
              <Badge className="rounded-full bg-amber-500/12 text-amber-700 border-amber-500/30 text-[10px]" data-testid="action-center-decisions">
                {counts.decisions} {t("actionCenter.decisions", { defaultValue: "to decide" })}
              </Badge>
            )}
            <Badge variant="outline" className="rounded-full text-[10px]" data-testid="action-center-total">
              {counts.total}
            </Badge>
          </div>
        )}
      </div>

      {!clear && (
        <ul className="mt-3.5 space-y-1.5">
          {items.map((it) => {
            const Icon = KIND_ICON[it.kind] || Flag;
            return (
              <li key={`${it.kind}-${it.id}`}>
                <button
                  type="button"
                  onClick={() => navigate(it.link)}
                  className="w-full flex items-center gap-2.5 rounded-xl border border-border bg-background/60 px-3 py-2 text-start hover:border-primary/40 hover:bg-accent/40 transition-colors"
                  data-testid={`action-center-item-${it.id}`}
                >
                  <Icon className={`h-3.5 w-3.5 shrink-0 ${it.overdue ? "text-rose-600" : "text-primary"}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate" dir="auto">
                      {it.title || t(`actionCenter.kind.${it.kind}`, { defaultValue: it.kind })}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      {t(`actionCenter.kind.${it.kind}`, { defaultValue: it.kind })}
                      {it.due_at ? ` · ${fmtDue(it.due_at, i18n.language)}` : ""}
                    </span>
                  </span>
                  {it.overdue && (
                    <span className="text-[10px] font-semibold text-rose-600 shrink-0">
                      {t("milestones.overdueLabel", { defaultValue: "Overdue" })}
                    </span>
                  )}
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 rtl-flip" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default ActionCenterCard;
